"use client";

import { ReactNode, useEffect, useState } from "react";
import { cn } from "@shared/src/utils/cn";

export type AnimationDirection = "fade" | "slide-up" | "slide-down" | "slide-left" | "slide-right";

interface QuickTradeAnimatedContainerProps {
  children: ReactNode;
  isVisible: boolean;
  animation?: AnimationDirection;
  duration?: number;
  delay?: number;
  className?: string;
  exitAnimation?: boolean | AnimationDirection;
}

export function QuickTradeAnimatedContainer({
  children,
  isVisible,
  animation = "fade",
  duration = 300,
  delay = 0,
  className = "",
  exitAnimation = true,
}: QuickTradeAnimatedContainerProps) {
  const [shouldRender, setShouldRender] = useState(isVisible);
  const [animationState, setAnimationState] = useState<"entering" | "exiting" | "idle">("idle");

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setTimeout(() => {
        setAnimationState("entering");
      }, delay);
    } else if (exitAnimation) {
      setAnimationState("exiting");
      setTimeout(() => {
        setShouldRender(false);
        setAnimationState("idle");
      }, duration);
    } else {
      setShouldRender(false);
    }
  }, [isVisible, delay, duration, exitAnimation]);

  if (!shouldRender) return null;

  const getAnimationClasses = () => {
    const baseClasses = "transition-all ease-in-out";
    const durationClass = `duration-${duration}`;

    // Determine which animation to use for each state
    const enterAnimation = animation;
    const exitAnimationDirection = typeof exitAnimation === "string" ? exitAnimation : animation;

    const getAnimationForDirection = (direction: AnimationDirection, isEntering: boolean) => {
      switch (direction) {
        case "fade":
          return isEntering ? "opacity-100" : "opacity-0";

        case "slide-up":
          return isEntering ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0";

        case "slide-down":
          return isEntering ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0";

        case "slide-left":
          return isEntering ? "translate-x-0 opacity-100" : "translate-x-4 opacity-0";

        case "slide-right":
          return isEntering ? "translate-x-0 opacity-100" : "-translate-x-4 opacity-0";

        default:
          return "opacity-100";
      }
    };

    if (animationState === "entering") {
      return cn(baseClasses, durationClass, getAnimationForDirection(enterAnimation, true));
    } else if (animationState === "exiting") {
      return cn(baseClasses, durationClass, getAnimationForDirection(exitAnimationDirection, false));
    } else {
      // Initial state (before entering)
      return cn(baseClasses, durationClass, getAnimationForDirection(enterAnimation, false));
    }
  };

  return <div className={cn(getAnimationClasses(), className)}>{children}</div>;
}
